import api from '@/lib/api';

/**
 * User Preference API Client
 * Handles cross-browser user preferences (column settings, UI state, etc.)
 */

export interface UserPreference {
  key: string;
  value: unknown;
  version: number;
  updated_at: string;
}

/**
 * Get user preference by key
 * @param key Preference key (e.g., 'workReports:columns:v1')
 * @returns Preference data or null if not found
 */
export const getMyPreference = async (key: string): Promise<UserPreference | null> => {
  const response = await api.get<{ success: boolean; data: UserPreference | null }>(
    `/users/me/preferences?key=${encodeURIComponent(key)}`
  );

  // Handle 401 specifically - don't throw, return null to allow fallback
  if (response.status === 401) {
    console.warn('[getMyPreference] 401 Unauthorized - token expired/invalid');
    return null;
  }

  if (!response.ok || !response.data?.success) {
    throw new Error(response.data?.message || 'Failed to get preference');
  }

  return response.data.data;
};

/**
 * Set/Update user preference
 * @param key Preference key
 * @param value Preference value (will be JSON stringified)
 * @param version Version number (default: 1)
 * @returns Updated preference data
 */
export const setMyPreference = async (
  key: string,
  value: unknown,
  version: number = 1
): Promise<UserPreference> => {
  const response = await api.put<{ success: boolean; data: UserPreference }>(
    '/users/me/preferences',
    {
      key,
      value,
      version,
    }
  );

  if (!response.ok || !response.data?.success) {
    throw new Error(response.data?.message || 'Failed to update preference');
  }

  return response.data.data;
};

/**
 * Delete user preference by key
 * @param key Preference key
 */
export const deleteMyPreference = async (key: string): Promise<void> => {
  const response = await api.delete<{ success: boolean }>(
    `/users/me/preferences?key=${encodeURIComponent(key)}`
  );

  if (!response.ok || !response.data?.success) {
    throw new Error(response.data?.message || 'Failed to delete preference');
  }
};

